import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { format } from "date-fns";
import {
  useCommissions,
  type CommissionFilters,
} from "@/hooks/use-commissions";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Percent,
} from "lucide-react";

import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/commissions/")({
  component: CommissionsPage,
});
function CommissionsPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <CommissionsPageContent />
      </AppShell>
    </ProtectedRoute>
  );
}


function CommissionsPageContent() {
  const [filters, setFilters] = useState<CommissionFilters>({
    page: 1,
    limit: 50,
  });

  const { data: commissions, isLoading } = useCommissions(filters);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      paid: "default",
      pending: "secondary",
      cancelled: "destructive",
    };

    return (
      <Badge variant={variants[status] || "default"}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  // Calculate summary stats
  const summary = commissions?.reduce(
    (acc: any, comm: any) => {
      acc.totalMerchantCommission += Number.parseFloat(comm.merchant_commission_amount || "0");
      acc.totalAgentCommission += Number.parseFloat(comm.agent_commission_amount || "0");
      acc.totalRevenue += Number.parseFloat(comm.net_revenue || "0");
      acc.totalOrderAmount += Number.parseFloat(comm.order_amount || "0");
      return acc;
    },
    { totalMerchantCommission: 0, totalAgentCommission: 0, totalRevenue: 0, totalOrderAmount: 0 }
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Commissions</h1>
          <p className="text-muted-foreground">
            Track commission earnings and revenue
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Order Amount
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ৳{summary.totalOrderAmount.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Merchant Commission
              </CardTitle>
              <TrendingDown className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                ৳{summary.totalMerchantCommission.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Agent Commission
              </CardTitle>
              <TrendingDown className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                ৳{summary.totalAgentCommission.toLocaleString()}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Net Revenue
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                ৳{summary.totalRevenue.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label>Status</Label>
              <Select
                value={filters.status || "all"}
                onValueChange={(value) =>
                  setFilters({ ...filters, status: value === "all" ? undefined : value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate || ""}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate || ""}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Commissions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Commission History</CardTitle>
          <CardDescription>
            {commissions?.length || 0} commission records found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : !commissions || commissions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No commissions found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Merchant</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Order Amount</TableHead>
                    <TableHead>Merchant Commission</TableHead>
                    <TableHead>Agent Commission</TableHead>
                    <TableHead>Net Revenue</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {commissions.map((commission: any) => (
                    <TableRow key={commission.id}>
                      <TableCell className="font-mono text-xs">
                        {commission.order_id.substring(0, 8)}...
                      </TableCell>
                      <TableCell className="font-medium">
                        {commission.merchant_name}
                      </TableCell>
                      <TableCell>
                        {commission.agent_name || (
                          <span className="text-muted-foreground">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center font-medium">
                          <DollarSign className="w-4 h-4 mr-1" />
                          {Number(commission.order_amount).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center text-red-600">
                            <TrendingDown className="w-3 h-3 mr-1" />
                            {Number(commission.merchant_commission_amount).toLocaleString()}
                          </div>
                          <div className="flex items-center text-xs text-muted-foreground">
                            <Percent className="w-3 h-3 mr-1" />
                            {Number(commission.merchant_commission_rate).toFixed(2)}%
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {commission.agent_commission_amount ? (
                          <div className="space-y-1">
                            <div className="flex items-center text-orange-600">
                              <TrendingDown className="w-3 h-3 mr-1" />
                              {Number(commission.agent_commission_amount).toLocaleString()}
                            </div>
                            <div className="flex items-center text-xs text-muted-foreground">
                              <Percent className="w-3 h-3 mr-1" />
                              {Number(commission.agent_commission_rate).toFixed(2)}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center font-bold text-green-600">
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {Number(commission.net_revenue).toLocaleString()}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(commission.status)}</TableCell>
                      <TableCell>
                        {format(new Date(commission.created_at), "PPp")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
